import { useCallback, useEffect, useState } from 'react'
import { clearRecentSearches, readRecentSearches, recordRecentSearch } from '../recent-searches-storage'

export type RecentSearches = {
  /** Most recent first, capped at the stored limit. */
  recents: string[]
  /** Remembers a committed query, moving a repeat back to the front. */
  record: (query: string) => void
  /** Forgets every query for this user and matter. */
  clear: () => void
}

/**
 * The queries this user has run against this matter, kept on the device.
 *
 * Held in `localStorage` rather than on the server: there is no endpoint for search history, and a
 * matter's file names carry client names and case details, so the query strings are worth keeping
 * off the wire until someone decides otherwise.
 */
export function useRecentSearches(matterId: string | null): RecentSearches {
  const [recents, setRecents] = useState<string[]>([])

  useEffect(() => {
    setRecents(matterId ? readRecentSearches(matterId) : [])
  }, [matterId])

  const record = useCallback(
    (query: string) => {
      if (!matterId) return
      setRecents(recordRecentSearch(matterId, query))
    },
    [matterId]
  )

  const clear = useCallback(() => {
    if (!matterId) return
    clearRecentSearches(matterId)
    setRecents([])
  }, [matterId])

  return { recents, record, clear }
}
